import { useState, useEffect } from 'react';
import { Edit, Save, X } from 'lucide-react';
import apiClient from '../api/client';

const CustomFieldsSection = ({ entityType, entityId }) => {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entityId) {
      loadFieldsAndValues();
    }
  }, [entityType, entityId]);

  const loadFieldsAndValues = async () => {
    try {
      setLoading(true);
      
      // Get all custom field values for this entity
      const response = await apiClient.get(`/api/v1/custom-fields/values/${entityType}/${entityId}`);
      const fieldValues = response.data || [];
      
      // Build fields array and values object
      const fieldsData = [];
      const valuesData = {};
      
      fieldValues.forEach(fv => {
        fieldsData.push({
          id: fv.field_id,
          name: fv.field_name,
          type: fv.field_type,
          is_required: false // We'll need to fetch field definitions separately if we need this
        });
        
        // Store value based on type
        if (fv.field_type === 'multi_select' && fv.value_json) {
          valuesData[fv.field_id] = fv.value_json;
        } else if (fv.field_type === 'date' && fv.value_date) {
          valuesData[fv.field_id] = fv.value_date;
        } else if (fv.field_type === 'number' && fv.value_numeric !== null) {
          valuesData[fv.field_id] = fv.value_numeric.toString();
        } else {
          valuesData[fv.field_id] = fv.value || '';
        }
      });
      
      setFields(fieldsData);
      setValues(valuesData);
      setEditedValues(valuesData);
    } catch (error) {
      console.error('Error loading custom fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedValues({ ...values });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedValues({ ...values });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Build payload
      const payload = Object.keys(editedValues).map(fieldId => ({
        field_id: fieldId,
        value: editedValues[fieldId]?.toString() || ''
      }));
      
      await apiClient.post(`/api/v1/custom-fields/values/${entityType}/${entityId}`, payload);
      
      // Reload to get updated values
      await loadFieldsAndValues();
      setIsEditing(false);
      alert('Custom fields updated successfully');
    } catch (error) {
      console.error('Error saving custom fields:', error);
      alert(error.response?.data?.detail || 'Failed to save custom fields');
    } finally {
      setSaving(false);
    }
  };

  const renderFieldValue = (field, value, isEditing) => {
    const fieldId = field.id;

    if (!isEditing) {
      // Display mode
      if (!value) {
        return <span className="text-gray-400 italic">Not set</span>;
      }

      switch (field.type) {
        case 'checkbox':
          return value === 'true' || value === true ? '✓ Yes' : '✗ No';
        case 'url':
          return (
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              {value}
            </a>
          );
        case 'email':
          return (
            <a href={`mailto:${value}`} className="text-blue-600 hover:underline">
              {value}
            </a>
          );
        case 'date':
          return new Date(value).toLocaleDateString();
        case 'multi_select':
          return Array.isArray(value) ? value.join(', ') : value;
        default:
          return value;
      }
    }

    // Edit mode
    switch (field.type) {
      case 'text':
      case 'email':
      case 'url':
        return (
          <input
            type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
            value={editedValues[fieldId] || ''}
            onChange={(e) => setEditedValues({ ...editedValues, [fieldId]: e.target.value })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            step="any"
            value={editedValues[fieldId] || ''}
            onChange={(e) => setEditedValues({ ...editedValues, [fieldId]: e.target.value })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={editedValues[fieldId] || ''}
            onChange={(e) => setEditedValues({ ...editedValues, [fieldId]: e.target.value })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        );
      
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={editedValues[fieldId] === 'true' || editedValues[fieldId] === true}
            onChange={(e) => setEditedValues({ ...editedValues, [fieldId]: e.target.checked ? 'true' : 'false' })}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
        );
      
      case 'dropdown':
        // For dropdown, we'd need the options from field definition
        // For now, just show a text input
        return (
          <input
            type="text"
            value={editedValues[fieldId] || ''}
            onChange={(e) => setEditedValues({ ...editedValues, [fieldId]: e.target.value })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={editedValues[fieldId] || ''}
            onChange={(e) => setEditedValues({ ...editedValues, [fieldId]: e.target.value })}
            className="w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm text-gray-600">Loading custom fields...</div>
      </div>
    );
  }

  if (fields.length === 0) {
    return null; // Don't show section if no fields defined
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Custom Fields</h3>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                title="Save changes"
              >
                <Save className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title="Edit custom fields"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Fields List */}
      <div className="p-4 space-y-3">
        {fields.map((field) => (
          <div key={field.id} className="grid grid-cols-3 gap-4 items-start">
            <div className="text-sm font-medium text-gray-700">{field.name}</div>
            <div className="col-span-2 text-sm text-gray-900">
              {renderFieldValue(field, values[field.id], isEditing)}
            </div>
          </div>
        ))}
      </div>

      {saving && (
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
          Saving changes...
        </div>
      )}
    </div>
  );
};

export default CustomFieldsSection;
